import React, { useState } from "react";
import { useUI } from "../context/UIContext";
import { TaskReportAPI } from "../api/apiClient";

export default function TaskReportView() {
  const { selectedProjectId } = useUI();

  const today = new Date().toISOString().split("T")[0];

  const [date, setDate] = useState(today);
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  const loadData = async () => {
    if (!selectedProjectId) return;

    setLoading(true);
    try {
      const res = await TaskReportAPI.getTaskReport(
        selectedProjectId,
        date
      );
      setData(res);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-xl space-y-6">
      <h2 className="text-xl font-bold">Task Report</h2>

      {/* DATE PICKER */}
      <div className="flex gap-2">
        <input
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          className="border p-2 rounded w-full"
        />
        <button
          onClick={loadData}
          className="px-4 py-2 bg-blue-600 text-white rounded"
        >
          Run
        </button>
      </div>

      {loading && <div>Loading...</div>}

      {/* ===================== SUMMARY ===================== */}
      {data?.summary && (
        <div className="bg-white rounded shadow">
          <div className="bg-blue-700 text-white p-2 font-bold">
            SUMMARY OF ISSUES
          </div>

          <Section
            title="On-Hold Tasks"
            count={data.summary.onHoldCount}
            items={data.summary.onHold}
          />

          <Section
            title="Not Started"
            count={data.summary.notStartedCount}
            items={data.summary.notStarted}
          />
        </div>
      )}

      {/* ===================== OVERDUE ===================== */}
      {data?.overdue?.length > 0 && (
        <div className="bg-white rounded shadow">
          <div className="bg-red-600 text-white p-2 font-bold">
            OverDue Tasks ({data.overdue.length})
          </div>

          {data.overdue.map((t: any) => (
            <div key={t.taskNo} className="p-2 border-b">
              <div className="font-medium">{t.taskName}</div>

              <div className="text-sm text-gray-600 flex justify-between">
                <span>
                  Due: {formatDate(t.endDate)}
                </span>
                <span>{t.progress}%</span>
              </div>

              <div className="text-xs text-gray-500">
                Logs: {t.totalLogs} | Last: {formatDate(t.lastLog)}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ===================== ACTIVE ===================== */}
      {data?.active?.length > 0 && (
        <SimpleList title="Active Tasks" items={data.active} color="green" />
      )}

      {/* ===================== NEW ===================== */}
      {data?.newTasks?.length > 0 && (
        <SimpleList title="New Tasks" items={data.newTasks} color="blue" />
      )}
    </div>
  );
}

/* ===================== REUSABLE COMPONENTS ===================== */

function Section({ title, count, items }: any) {
  return (
    <div>
      <div className="flex justify-between px-2 py-1 font-semibold text-sm">
        <span>{title}</span>
        <span>{count}</span>
      </div>

      {items.map((t: any) => (
        <div key={t.taskNo} className="px-2 py-1 border-t text-sm">
          <div className="flex justify-between">
            <span>{t.taskName}</span>
            <span>{formatDate(t.endDate)}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

function SimpleList({ title, items, color }: any) {
  const bg =
    color === "green"
      ? "bg-green-600"
      : color === "blue"
      ? "bg-blue-600"
      : "bg-gray-600";

  return (
    <div className="bg-white rounded shadow">
      <div className={`${bg} text-white p-2 font-bold`}>
        {title} ({items.length})
      </div>

      {items.map((t: any) => (
        <div key={t.taskNo} className="p-2 border-b text-sm">
          <div className="flex justify-between">
            <span>{t.taskName}</span>
            <span>{formatDate(t.endDate)}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

function formatDate(date: string) {
  if (!date) return "";
  return new Date(date).toLocaleDateString();
}